/**
 * Where this copy of the board keeps its data.
 *
 * The same names in every environment, because each environment is its own
 * Firebase project. Which project is chosen in src/firebase.ts, from the
 * build's own stamp.
 */
/*
 * NO PREFIX ANY MORE — the environments are separate Firebase projects.
 *
 * Every collection here used to be written twice: `issues` on the real board
 * and `uat_issues` beside it in the SAME database, told apart by a name. That
 * is a convention, not a boundary. One set of rules governed both, one deploy
 * changed both, and `access`, `roles` and `sessions` were not even prefixed —
 * they were the same documents.
 *
 * Each environment now has its own project, so the names are the same in both
 * and cannot collide: there is no connection between the two databases for
 * anything to cross.
 */
export const C = {
  projects: 'projects',
  // The flat collections these replaced. Still read as a fallback while the
  // move is being trusted; nothing writes to them any more.
  issues: 'issues',
  builds: 'builds',
  board: 'board',
  access: 'access',
  roles: 'roles',
  audit: 'audit',
  profiles: 'profiles',
  sessions: 'sessions',
  /** Teams. Who is IN one is kept on the person — see src/groups.ts. */
  groups: 'groups',
  /** Requests that an admin add somebody to a board — #359. Tenant-scoped;
      RLS decides who reads which. */
  boardRequests: 'boardRequests',
  /** A person's call-outs — the feed behind the bell (track A, #388).
      Tenant-scoped so the bell gathers every board they are on; RLS narrows the
      list to the rows they own, and only the database writes them (0104, 0105). */
  notifications: 'notifications',
  /** Who covers a signer while they are away — configurable sign-offs R1 (0130).
      Tenant-scoped (a cover spans every board the person is on); RLS keeps it to
      the tenant, and a person may write only their own unless they administer. */
  signoffCover: 'signoffCover',
  /** The workspace (tenant) row itself — read for its currency, written by an
      admin to change it (0117). Keyed by the tenant id from the sign-in token. */
  workspace: 'workspace',
  /** Light reactions on card-feed entries — emoji on a comment or update (0143).
      Read like notifications: one listener for the whole workspace, RLS narrows
      it to reactions on boards the person can open, and the app merges them onto
      the feed by the entry id they point at. */
  reactions: 'reactions',
  /** The lessons register — 3.19 Learning & Governance spine (L1). Read like
      reactions: one listener for the whole workspace, RLS hands back workspace-wide
      lessons plus board lessons on boards the person can open. The Lessons tab
      filters to its board; the library shows them all. board_id is nullable. */
  lessons: 'lessons',
  /** A sign-in's network origin — IP and an approximate city/country, captured
      server-side and kept for admins only (0150). One listener for the whole
      workspace, like reactions; RLS returns rows only to an admin (and nothing
      to the person a row is about), and the app merges each onto its sign-in
      line by the audit id it points at. Purged after 90 days. */
  auditOrigin: 'auditOrigin',
} as const;


/** Where one project keeps its issues, builds and sprints. */
export const inProject = (projectId: string) => ({
  issues: `${C.projects}/${projectId}/issues`,
  /*
   * The parts of the product this board's work divides into — #269. Under the
   * board for the same reason as everything else here, and because they ARE the
   * board's: TRINRE's areas are not FlightDeck's and never will be.
   */
  areas: `${C.projects}/${projectId}/areas`,
  /*
   * What sort of work a card is, in this board's own words — #302. Under the
   * board for the same reason areas are, and because they are the board's: a
   * customer's "Incident" is not ours, and neither is the next customer's.
   */
  issueTypes: `${C.projects}/${projectId}/issueTypes`,
  /*
   * The board's managed labels — a cross-cutting tag, independent of area or
   * epic, that a board can name and optionally colour (#462 follow-on). Under
   * the board like areas and types, and because they ARE the board's: one
   * board's "needs vendor" is not another's.
   */
  labels: `${C.projects}/${projectId}/labels`,
  /*
   * The stages this project runs in — #377. Under the board like everything
   * else here, and because they ARE the board's: a data migration's phases are
   * not an ERP rollout's, and neither is the next customer's.
   */
  phases: `${C.projects}/${projectId}/phases`,
  /*
   * The Plan view's two collections — #377. The fluid steps worked inside a
   * phase, and the append-only history of who signed and changed what. Flat per
   * board like phases, filtered to a phase in the app, so a listener watches one
   * collection rather than one per phase.
   */
  phaseSteps: `${C.projects}/${projectId}/phaseSteps`,
  phaseEvents: `${C.projects}/${projectId}/phaseEvents`,
  /*
   * The board's standalone milestones — #374 + milestone payments. Flat per board
   * like phases; a billable one is a payment point invoices claim against. Under
   * the board for the same reason as everything else here.
   */
  milestones: `${C.projects}/${projectId}/milestones`,
  /*
   * The project's decision log — 3.19 Learning & Governance spine (D1). Flat per
   * board like phases and milestones, and under the board because a decision is the
   * board's: what one project decided is not another's. Arrives live so a logged
   * decision shows for everyone at once.
   */
  decisions: `${C.projects}/${projectId}/decisions`,
  /*
   * The project's RAID register — Risks, Assumptions, Issues and Dependencies
   * (migration 0193). Four first-class collections flat per board, like decisions
   * and milestones, and under the board because a project's risks are its own. Each
   * arrives live so a logged item shows for everyone at once. The register is the
   * single source of truth; the charter's Risk-register section renders from it.
   */
  risks: `${C.projects}/${projectId}/risks`,
  assumptions: `${C.projects}/${projectId}/assumptions`,
  registerIssues: `${C.projects}/${projectId}/registerIssues`,
  dependencies: `${C.projects}/${projectId}/dependencies`,
  builds: `${C.projects}/${projectId}/builds`,
  /*
   * Under the board rather than in one pile, for the reason every other
   * collection here is: a permission rule can then tell which board a sprint
   * belongs to from its path alone, without fetching it first to find out.
   */
  sprints: `${C.projects}/${projectId}/sprints`,
  /*
   * The signatures on this board's releases. Under the board for the same
   * reason as the rest — a rule can tell which board a row belongs to from its
   * path — and separate from `builds` because a release is edited and a
   * signature is not.
   */
  signoffs: `${C.projects}/${projectId}/signoffs`,
  /*
   * The documents this project is delivered against — #193. Under the board
   * for the same reason as everything else here: a rule can tell which board a
   * row belongs to from its path, without fetching it to find out.
   */
  documents: `${C.projects}/${projectId}/documents`,
});
